import React, { useState } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import usePlanReportStore from '@/store/hr/PlanReportStore';

interface ExportMenuProps {
  report: 'board' | 'employees' | 'slippage';
  filters: Record<string, any>;
  disabled?: boolean;
}

const FORMATS = [
  { key: 'csv' as const, label: 'CSV', icon: <DescriptionIcon fontSize="small" /> },
  { key: 'xlsx' as const, label: 'Excel (.xlsx)', icon: <TableChartIcon fontSize="small" /> },
  { key: 'pdf' as const, label: 'PDF', icon: <PictureAsPdfIcon fontSize="small" /> }
];

/**
 * Export is generated SERVER-SIDE and covers the whole period — not the page you
 * are looking at, and not recomputed in the browser. The file and the screen call
 * the same service function, so they cannot disagree.
 */
const ExportMenu: React.FC<ExportMenuProps> = ({ report, filters, disabled }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const exportReport = usePlanReportStore((s) => s.exportReport);
  const exporting = usePlanReportStore((s) => s.exporting);

  const run = async (format: 'csv' | 'xlsx' | 'pdf') => {
    setAnchorEl(null);
    await exportReport(report, format, filters);
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={disabled || !!exporting}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        {exporting ? 'Preparing…' : 'Export'}
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {FORMATS.map((f) => (
          <MenuItem key={f.key} onClick={() => run(f.key)}>
            <ListItemIcon>{f.icon}</ListItemIcon>
            <ListItemText>{f.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ExportMenu;
